<template>
  <div class="mb-4">
    <div class="mt-4 mb-4">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item"><a href="/">{{ $t('home') }}</a></li>
          <li class="breadcrumb-item"><a :href="$t('ppd-title-link')">{{ $t('ppd-title') }}</a></li>
          <li class="breadcrumb-item"><a :href="$t('building-development-title-link')">{{ $t('building-development-title') }}</a></li>
          <li class="breadcrumb-item"><a :href="$t('permit-statistics-title-link')">{{ $t('permit-statistics-title') }}</a></li>
          <li class="breadcrumb-item active" aria-current="page">{{ $t('development-volume-title') }}</li>
        </ol>
      </nav>
    </div>
    <h1 class="mb-4">{{ $t("development-volume-title") }}</h1>    
    <h2 class="mb-4">{{ $t('development-volume-subtitle') }}</h2>
    <p class="mb-4">{{ $t('development-volume-description') }}</p>

    <DatePicker v-if="start_date && end_date" v-on:set-year="handleYear" :from_date="start_date" :to_date="end_date" :set_year="selected_year" :optional_months="false"></DatePicker>

    <div class="accordion mt-4" id="report-qualifications">
      <div class="card">
    <div class="card-header" id="headingOne">
      <div class="heading" role="button" data-toggle="collapse" data-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
        {{ $t('report-qualifications') }}
        <i class="far fa-chevron-down"></i>
      </div>
    </div>
    <div id="collapseOne" class="collapse mb-4" aria-labelledby="headingOne">
      <div class="card-body">
        <ul>
          <li>{{ $t('development-permit-types-not-included') }}</li>
          <li>{{ $t('development-residential-permit-activity-description') }}</li>
          <li>{{ $t('development-activity-do-not-include') }}</li>
        </ul>
        <p><strong>{{ $t('disclaimer') }}</strong><br /> {{ $t('disclaimer-text') }}</p>
        </div>
      </div>
    </div>      
    </div>
    <div v-if="hasYearParam">
        <h3 class="mt-4">{{ $t('development-permit-volume-for', { text_date: textDate }) }}</h3>
        <permit-activity 
          :selected-year="selected_year" 
          :filter-function="housingFilter"
          :issues-filter-function="housingFilter"
          :permit-applications="dataByYear[selected_year]"
          :permits-issued="issuesByYear[selected_year]"
          graph-title="residential-permit-activity-chart-title"
          class="mt-4"
        />
        <br />
        <permit-activity 
          :selected-year="selected_year" 
          :filter-function="commercialFilter"
          :issues-filter-function="commercialFilter"
          :permit-applications="dataByYear[selected_year]"
          :permits-issued="issuesByYear[selected_year]"
          graph-title="commercial-permit-activity-chart-title"
          class="mt-4"
        />
        <br />
        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          graph-title="cumulative-housing-commercial-chart-title"
          class="mt-4"
        />
        <br />
        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingFilter"
          graph-title="cumulative-housing-chart-title"
          class="mt-4"
        />
        <br />
        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="commercialFilter"
          graph-title="cumulative-commercial-chart-title"
          class="mt-4"
        />
        <br />
        <activity-and-linear 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="chainFilters(housingFilter, newAndAdditionsFilter)"
          graph-title="housing-new-and-additions-chart-title"
          class="mt-4"
        />
        <br />
        <activity-and-linear 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="chainFilters(housingFilter, changeOfUseFilter)"
          graph-title="housing-change-of-use-chart-title"
          class="mt-4"
        />
        <br />
        <activity-and-linear 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="chainFilters(housingFilter, accessoryStructureFilter)"
          graph-title="housing-accessory-structures-chart-title"
          class="mt-4"
        />
        <br />
        <activity-and-linear 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="chainFilters(commercialFilter, newAndAdditionsFilter)"
          graph-title="commercial-new-and-additions-chart-title"
          class="mt-4"
        />
        <br />
        <activity-and-linear 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="chainFilters(commercialFilter, changeOfUseFilter)"
          graph-title="commercial-change-of-use-chart-title"
          class="mt-4"
        />

    </div>
    <p class="mb-4"><a href="https://data.winnipeg.ca/Development-Approvals-Building-Permits-Inspections/Development-permits-application-counts/wbyv-ks6y/about_data">{{ $t('data-source') }}</a></p>

  </div>
</template>

<script>
import DatePicker from '@/components/DatePicker';
import PermitActivity from '@/components/PermitActivity';
import CumulativeHousingAndCommercial from '@/components/CumulativeHousingAndCommercial';
import ActivityAndLinear from '@/components/ActivityAndLinear';
import axios from 'axios';
export default {
  name: 'DevelopmentVolume',
  components: {
    DatePicker,
    PermitActivity,
    CumulativeHousingAndCommercial,
    ActivityAndLinear,
  },
  data() {
    return {
      start_date: null,
      end_date: null,
      selected_year: "",
      application_data: [],
      issues_data: [],
    }
  },

  async created() {
    if (this.$route.params.year) {
      this.selected_year = "" + this.$route.params.year;
    }
    const url = this.$DEVELOPMENT_PERMIT_API + "?$select=min(issue_date) as start_date,max(issue_date) as end_date";
    const { data } = await axios.get(url);
    if (data.length) {
      const minDate = new Date(data[0].start_date);
      this.start_date = new Date(minDate.setFullYear(minDate.getFullYear() + 1)); // 2022 didn't have a lot of data
      this.end_date = new Date(data[0].end_date);
    }
    this.fetchData();
  },

  computed: {
    hasYearParam() {
      return this.$route.params.year && this.$route.params.year.length > 0;
    },

    textDate() {
      if (!this.hasYearParam) return '';
      const cur_date = new Date(this.curYear, 0); // January of the selected year
      const options = { year: 'numeric' };
      return cur_date.toLocaleDateString('en-CA', options);
    },
    curYear() {
      return this.$route.params.year ? this.$route.params.year + "" : "";
    },
    issuesByYear() {
      return this.byYear(this.issues_data, 'app_start_month');
    },
    dataByYear() {
      return this.byYear(this.application_data, 'app_start_month');
    },
  },

  methods: {
     chainFilters(...filters) {
      const activeFilters = filters.filter(Boolean);
      if (activeFilters.length === 0) {
        return () => true;
      }
      return item => activeFilters.every(filterFn => filterFn(item));
    },
     byYear(data, key) {
      let byYear = {};
      if (data && data.length) {
        data.forEach(item => {
          let start_date = new Date(item[key]);
          const year = start_date.getFullYear();
          if (!byYear[year]) {
            byYear[year] = [];
          }
          byYear[year].push(item);
        });
      }
      return byYear;
    },
    housingFilter(item) {
      return item.category && item.category.toLowerCase().includes('residential');
    },

    commercialFilter(item) {
      return item.category && item.category.toLowerCase().includes('commercial');
    },

    changeOfUseFilter(item) {
      return item.application_type
        && item.application_type.toLowerCase().includes('change of use');
    },

    newAndAdditionsFilter(item) {
      return item.application_type
        && item.application_type.toLowerCase().includes('new and additions');
    },
    
    accessoryStructureFilter(item) {
      return item.application_type
        && item.application_type.toLowerCase().includes('accessory structure');
    },

    handleYear(value) {
      if (value) {
        this.selected_year = "" + value;
      } else {
        this.selected_year = "";
      }
      this.handleDate();
    },

    handleDate() {
      const targetRoute = this.selected_year 
        ? { name: 'development-volume', params: { year: this.selected_year } }
        : { name: 'development-volume' };
      this.$router.replace(targetRoute);
    },

    async fetchData() {
      let dpData = [];
      try {
        if (this.$DEVELOPMENT_VOLUME_API) {
          const application_url = this.$DEVELOPMENT_VOLUME_API + "?$limit=10000&$order=app_start_month asc";
          const { data } = await axios.get(application_url);
          if (data.length) {
            this.application_data = data;
          }
        } else {
          throw new Error('DEVELOPMENT_VOLUME_API is not set');
        }
      } catch (e) {
        // fall back to asset json if api fails
        try {
          dpData = (await import(/* webpackChunkName: "dp_application_counts" */ '@/assets/dp_application_counts.json')).default;
        } catch (assetErr) {
          dpData = [];
        }
        this.application_data = dpData;
      }

      // Now fetch issues_data from API as before (or set empty if not present)
      try {
        const url = this.$DEVELOPMENT_PERMIT_API + "?$limit=10000&$select=date_trunc_ym(issue_date) as app_start_month, sub_type, category, subcategory as application_type, count(*) as total_count&$group=date_trunc_ym(issue_date), permit_group, permit_type, sub_type, category, subcategory&$order=date_trunc_ym(issue_date)&$where=permit_group = 'Land Applications' AND permit_type = 'Development Permits' and category is not null and subcategory is not null";
        const { data } = await axios.get(url);
        if (data.length) {
          this.issues_data = data;
        } else {
          this.issues_data = [];
        }
      } catch (e) {
        this.issues_data = [];
      }
    }
  }
}
</script>